import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { psychologistServiceBaseUrl } from '../configs/api/api.config';
import { RootState } from '../store/store';
import { logout, setCredentials } from '../store/auth.slice';

export interface CatalogDictItem {
  id: string;
  title: string;
  sort: number | null;
  status: 'published' | 'draft';
}

export interface CatalogLanguageItem {
  code: string;
  name: string;
  direction: string | null;
  sort: number | null;
  status: 'published' | 'draft';
}

export interface CatalogGenderItem {
  id: string;
  gender: string;
  gender_en: string;
  sort: number | null;
  status: 'published' | 'draft';
}

export interface CatalogCurrencyItem {
  id: string;
  short_name: string | null;
  long_name: string;
  currency_symbol: string;
  sort: number | null;
  status: 'published' | 'draft';
}

export interface CatalogProfileDictionaries {
  formats: CatalogDictItem[];
  issues: CatalogDictItem[];
  languages: CatalogLanguageItem[];
  modalities: CatalogDictItem[];
  specialization: CatalogDictItem[];
  working_with: CatalogDictItem[];
  genders: CatalogGenderItem[];
  religions: CatalogDictItem[];
  education_types: CatalogDictItem[];
  currencies: CatalogCurrencyItem[];
}

export interface CatalogProfilePhoto {
  id: string;
  file_id: string;
  url: string;
}

export interface CatalogProfile {
  status: 'published' | 'draft' | 'ready_for_review' | 'rejected' | 'manual_review';
  // Публичная карточка остаётся доступна, пока её новая ревизия проходит проверку.
  has_published_profile: boolean;
  moderation?: {
    reason_category: string | null;
    user_message: string | null;
  };
  first_name: string;
  last_name: string;
  middle_name: string;
  title: string;
  age: number | null;
  city: string;
  gender: string;
  specialization: string[];
  experience_years: number | null;
  profile_photo: CatalogProfilePhoto | null;
  price: number | null;
  price_offline: number | null;
  currency: string;
  duration_minutes: number | null;
  formats: string[];
  languages: string[];
  religion: string;
  about_me: string;
  education_description: string;
  education_type: string;
  diploma_verified: boolean;
  modalities: string[];
  issues: string[];
  working_with: string[];
  phone_1: string;
  phone_2: string;
  telegram: string;
  vk: string;
  whatsup: string;
  b17_profile: string;
  work_on_yasno: boolean;
  work_on_alter: boolean;
  work_on_meta: boolean;
  work_on_zigmung: boolean;
}

export interface CatalogProfileMeResponse {
  exists: boolean;
  /** ID одобренной публичной версии; не входит в payload редактируемой формы. */
  publicProfileId: string | null;
  data: CatalogProfile | null;
}

export interface B17ImportSuggestion {
  id?: string;
  title?: string;
  source_text?: string;
  unmatched?: string;
  confidence: number;
}

export interface B17ImportProfileData {
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  title: string | null;
  about_me: string | null;
  education_description: string | null;
  city: string | null;
  experience_years: number | null;
  price: number | null;
  duration_minutes: number | null;
  b17_profile: string;
  suggested_modalities: B17ImportSuggestion[];
  suggested_issues: B17ImportSuggestion[];
  suggested_specialization: B17ImportSuggestion[];
  confidence: Record<string, number>;
}

const baseQuery = fetchBaseQuery({
  baseUrl: psychologistServiceBaseUrl,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = (getState() as RootState).authSlice.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    // multipart/form-data boundary must be set by the browser, not overridden here
    if (endpoint !== 'uploadCatalogProfilePhoto') {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && (result.error as any).status === 401) {
    const refreshToken = (api.getState() as RootState).authSlice.refreshToken;
    try {
      const refreshResult: any = await fetchBaseQuery({ baseUrl: psychologistServiceBaseUrl })(
        { url: '/token/refresh', method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }) },
        api,
        extraOptions,
      );
      if (refreshResult?.data) {
        api.dispatch(setCredentials({ ...refreshResult.data }));
        result = await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(logout());
      }
    } catch {
      api.dispatch(logout());
    }
  }

  return result;
};

export const catalogProfileApi = createApi({
  reducerPath: 'catalogProfileApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['CatalogProfile', 'CatalogProfileDictionaries', 'CatalogProfilePhoto'],
  endpoints: (build) => ({
    getCatalogProfileMe: build.query<CatalogProfileMeResponse, void>({
      query: () => '/catalog-profile/me',
      transformResponse: (raw: {
        data: { exists: boolean; id: unknown; email: unknown } & Partial<CatalogProfile>;
      }): CatalogProfileMeResponse => {
        const { exists, id, email: _email, ...profile } = raw.data;
        const publicProfileId = typeof id === 'string' || typeof id === 'number' ? String(id) : null;
        return { exists, publicProfileId, data: exists ? (profile as CatalogProfile) : null };
      },
      providesTags: ['CatalogProfile'],
    }),

    updateCatalogProfileMe: build.mutation<CatalogProfile, Partial<CatalogProfile>>({
      query: (body) => ({
        url: '/catalog-profile/me',
        method: 'PUT',
        body: JSON.stringify(body),
      }),
      invalidatesTags: ['CatalogProfile'],
    }),

    submitCatalogProfile: build.mutation<CatalogProfile, void>({
      query: () => ({ url: '/catalog-profile/me/submit', method: 'POST' }),
      invalidatesTags: ['CatalogProfile'],
    }),

    withdrawCatalogProfile: build.mutation<CatalogProfile, void>({
      query: () => ({ url: '/catalog-profile/me/withdraw', method: 'POST' }),
      invalidatesTags: ['CatalogProfile'],
    }),

    unpublishCatalogProfile: build.mutation<CatalogProfile, void>({
      query: () => ({ url: '/catalog-profile/me/unpublish', method: 'POST' }),
      invalidatesTags: ['CatalogProfile'],
    }),

    getCatalogProfileDictionaries: build.query<CatalogProfileDictionaries, void>({
      query: () => '/catalog-profile/dictionaries',
      transformResponse: (raw: { data: CatalogProfileDictionaries }): CatalogProfileDictionaries => raw.data,
      providesTags: ['CatalogProfileDictionaries'],
    }),

    getCatalogProfilePhoto: build.query<Blob, void>({
      query: () => ({
        url: '/catalog-profile/me/photo',
        responseHandler: (response: Response) => response.blob(),
      }),
      providesTags: ['CatalogProfilePhoto'],
    }),

    uploadCatalogProfilePhoto: build.mutation<CatalogProfile, FormData>({
      query: (formData) => ({
        url: '/catalog-profile/me/photo',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (raw: { data: CatalogProfile }): CatalogProfile => raw.data,
      invalidatesTags: ['CatalogProfile', 'CatalogProfilePhoto'],
    }),

    importCatalogProfileB17: build.mutation<B17ImportProfileData, { url: string }>({
      query: (body) => ({
        url: '/catalog-profile/import/b17',
        method: 'POST',
        body: JSON.stringify(body),
      }),
      transformResponse: (raw: { data: B17ImportProfileData }): B17ImportProfileData => raw.data,
    }),

    deleteCatalogProfilePhoto: build.mutation<void, void>({
      query: () => ({
        url: '/catalog-profile/me/photo',
        method: 'DELETE',
      }),
      invalidatesTags: ['CatalogProfile'],
    }),
  }),
});

export const {
  useGetCatalogProfileMeQuery,
  useUpdateCatalogProfileMeMutation,
  useSubmitCatalogProfileMutation,
  useWithdrawCatalogProfileMutation,
  useUnpublishCatalogProfileMutation,
  useGetCatalogProfileDictionariesQuery,
  useGetCatalogProfilePhotoQuery,
  useUploadCatalogProfilePhotoMutation,
  useDeleteCatalogProfilePhotoMutation,
  useImportCatalogProfileB17Mutation,
} = catalogProfileApi;
